// DealColumn — una etapa del tablero (zona de soltar). Encabezado: nombre,
// color de la etapa, número de deals y el monto del pipeline que reporta el
// SERVIDOR (forecast por etapa). Ganado/Perdido no traen total en el forecast:
// se muestra solo el conteo (el cliente nunca suma dinero).

import { useState } from 'react';

import { fmtMoneyShort, fmtMoney } from '../model';
import type { Deal, DealStage, FollowUpRow, ForecastStageBucket } from '../types';
import { DealCard } from './DealCard';
import { stageStyle } from './parts';

interface DealColumnProps {
  stage: DealStage;
  deals: Deal[];
  stages: DealStage[];
  bucket?: ForecastStageBucket;
  /** El forecast respondió (sin bucket = $0 abiertos en la etapa). */
  forecastReady: boolean;
  currency: string;
  followUps: Record<string, FollowUpRow>;
  activeId: string | null;
  draggingId: string | null;
  isMobile: boolean;
  isSeller: boolean;
  onOpen: (d: Deal) => void;
  onMove: (d: Deal, s: DealStage) => void;
  onDragStartDeal: (d: Deal) => void;
  onDragEndDeal: () => void;
  onDropDeal: (stage: DealStage) => void;
  emptyHint?: string;
}

export function DealColumn(p: DealColumnProps) {
  const { stage, deals, bucket, currency, isMobile } = p;
  const [over, setOver] = useState(false);
  const amount = stage.kind === 'open' ? (bucket ? bucket.total_value : p.forecastReady ? '0' : null) : null;
  return (
    <section
      aria-label={`Etapa ${stage.name}, ${deals.length} deals`}
      style={stageStyle(stage)}
      data-testid={`deal-column-${stage.slug}`}
      onDragOver={(e) => { if (p.draggingId) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false); }}
      onDrop={(e) => { e.preventDefault(); setOver(false); p.onDropDeal(stage); }}
      className={`dl-col-top flex min-h-0 min-w-0 flex-col rounded-2xl border border-border bg-muted/40 ${isMobile ? 'h-full' : 'w-[272px] shrink-0 xl:w-auto xl:min-w-[232px] xl:flex-1'} ${over ? 'dl-drop' : ''}`}
    >
      {(
        <header className="px-3 pb-2 pt-3">
          <div className="flex items-center gap-2">
            <span className="dl-dot h-2.5 w-2.5 shrink-0 rounded-full" aria-hidden />
            <h3 className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-foreground">{stage.name}</h3>
            <span className="rounded-full bg-card px-2 py-0.5 text-[11.5px] font-semibold tabular-nums text-foreground ring-1 ring-border">{deals.length}</span>
          </div>
          <p className="mt-1 truncate pl-[18px] text-[12px] tabular-nums text-muted-foreground"
            title={amount !== null && bucket ? `Total ${fmtMoney(bucket.total_value, currency)} · ponderado ${fmtMoney(bucket.weighted_value, currency)}` : undefined}>
            {amount !== null
              ? <>{fmtMoneyShort(amount, currency)}{bucket ? <> · pond. {fmtMoneyShort(bucket.weighted_value, currency)}</> : null}</>
              : stage.kind === 'won' ? 'Cerrados ganados' : stage.kind === 'lost' ? 'Cerrados perdidos' : 'Monto no disponible'}
          </p>
        </header>
      )}
      <div className="dl-scroll min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {deals.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border px-3 py-6 text-center text-[12.5px] text-muted-foreground">
            {p.emptyHint || (isMobile ? `Nada en ${stage.name}.` : 'Arrastra aquí un deal o usa «Mover a…».')}
          </p>
        ) : (
          <ul className="space-y-2">
            {deals.map((d) => (
              <DealCard key={d.id} deal={d} stages={p.stages} followUp={p.followUps[d.id]} active={p.activeId === d.id}
                draggable={!isMobile} mine={p.isSeller} onOpen={p.onOpen} onMove={p.onMove}
                onDragStart={p.onDragStartDeal} onDragEnd={p.onDragEndDeal} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
